import type {
  LabelRecordMap,
  LocationRecordMap
} from '@libs/data-access/entities'
import { Alert, AlertErrorAdapter, Modal, usePrevious } from '@libs/ui/ds'
import { BaseForm } from '@libs/ui/form'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import type { ReduxState } from '@spa/redux/ReduxState'
import { useMemoSelector } from '@spa/redux/hooks'
import { LockedAlert } from '@spa/scenes/Plannings/Forms/components'
import Immutable from 'immutable'
import { isEqual } from 'lodash-es'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import type { FC } from 'react'
import { useSelector } from 'react-redux'
import { getContractById } from 'snap-redux/modules/contracts/selectors'
import { getLabelsByTeamId } from 'snap-redux/modules/labels/selectors'
import { getLocationById } from 'snap-redux/modules/locations/selectors'
import { getRoleRights } from 'snap-redux/modules/memberships/utils'
import { getShiftById } from 'snap-redux/modules/shifts/selectors'
import { getTeamById } from 'snap-redux/modules/teams/selectors'
import { getWeeklyScheduleById } from 'snap-redux/modules/weeklyschedules/selectors'

import { LabelSelectLegacy } from '../components/LabelField/LabelSelectLegacy'

import styles from './ShiftFormLegacy.module.css'
import {
  baseStructuredSelector,
  currentUserSelector
} from './ShiftFormLegacy.selectors'
import {
  AutomaticBreakRowsContainer,
  NotifyUser,
  ShiftActions,
  ShiftNote,
  ShiftTimeRangeInputs,
  UpdateDayFieldLegacy,
  UserContractFieldLegacy,
  WeekdaysFields
} from './components'
import { LocationAndTeamFieldsLegacy } from './components/LocationAndTeamFieldsLegacy'
import { MealRowsContainerLegacy } from './components/MealRowsContainer/MealRowsContainerLegacy'
import { ShowMore } from './components/ShowMore/ShowMore'
import { useShiftFormLegacy } from './hooks'
import { useDefaultLabel } from './hooks/useDefaultLabel'
import { useDisplayEndContractOverrunWarning } from './hooks/useDisplayEndContractOverrunWarning'
import { useFloatingBreaksWarning } from './hooks/useDisplayFloatingBreaksWarning'
import { getDefaultShiftModelLegacy } from './utils'

type Props = {
  onFormSuccess: () => void
  date?: string | Date
  shiftId?: number
  userContractId?: number
  weeklyscheduleId?: number
  labelId?: number
}
/**
 * @deprecated use the new one with RQ
 */
export const ShiftFormLegacy: FC<Props> = ({
  onFormSuccess,
  date,
  shiftId,
  userContractId,
  weeklyscheduleId,
  labelId
}) => {
  const { t } = usePlanningFormTranslation()
  const defaultLabel = useDefaultLabel()
  const [showingMore, setShowingMore] = useState(false)

  const { account, currentUserId, weeklyschedules } = useMemoSelector(
    baseStructuredSelector
  )

  const shift =
    useMemoSelector(state => getShiftById(state, shiftId)) ||
    getDefaultShiftModelLegacy({
      // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
      userContractId,
      // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
      weeklyscheduleId,
      // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
      date,
      defaultMeal: account.meal_by_default_for_real
    })

  const weeklyschedule = useMemoSelector(state =>
    getWeeklyScheduleById(state, weeklyscheduleId)
  )

  const {
    week: weeklyScheduleWeek,
    published: weeklyScheduleIsPublished,
    team_id: weeklyScheduleTeamId
  } = weeklyschedule

  const { currentMembership } = useSelector(
    (state: ReduxState) => currentUserSelector(state, currentUserId),
    isEqual
  )

  const filteredWeeklyschedules = weeklyschedules.filter(
    // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
    wks => wks.week === weeklyScheduleWeek
  )

  const weeklyscheduleFromShift = filteredWeeklyschedules
    // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
    .filter(fWks => fWks.id === shift.weeklyschedule_id)
    .toList()
    .first()

  const selectedTeamLocation = useMemoSelector(state => {
    const teamFromWeeklyschedule = getTeamById(
      state,
      weeklyscheduleFromShift?.team_id
    )
    return (
      weeklyscheduleFromShift?.team_id &&
      getLocationById(state, teamFromWeeklyschedule?.location_id)
    )
  })

  const weeklyscheduleTeam = useSelector(
    (state: ReduxState) =>
      weeklyScheduleTeamId && getTeamById(state, weeklyScheduleTeamId),
    isEqual
  )

  const weeklyscheduleLocation = useSelector(
    (state: ReduxState) =>
      weeklyscheduleTeam &&
      getLocationById(state, weeklyscheduleTeam.location_id),
    isEqual
  )

  const { atLeast } = getRoleRights(currentMembership)

  const isCreation = !shift?.id

  const [teamId, setTeamId] = useState(
    weeklyscheduleFromShift?.team_id || weeklyScheduleTeamId
  )

  const methods = useShiftFormLegacy({
    labelId,
    shift,
    teamId,
    weeklyScheduleIsPublished,
    // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
    selectedTeamLocation,
    weeklyscheduleLocation,
    weeklyScheduleWeek,
    onFormSuccess
  })

  const {
    watch,
    setValue,
    onSubmit,
    formError,
    control,
    formState: { errors },
    formLoading,
    onDeleteSubmit
  } = methods

  const userContracts = watch('user_contract')
  const selectedDays = watch('days')
  const selectedTeamId = Number(watch('team')?.value) || teamId
  const selectedLocationId =
    // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
    Number(watch('location')?.value) || selectedTeamLocation?.id
  const selectedContractId =
    Number(userContracts?.[0]?.value) || shift?.user_contract_id

  const contract = useMemoSelector(state =>
    getContractById(state, selectedContractId)
  )
  const isUpdatingContract = selectedContractId !== shift?.user_contract_id

  const handleToggleLocation = () => setShowingMore(!showingMore)

  const teams = useSelector(
    (state: ReduxState) =>
      (contract &&
        contract.team_ids
          // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
          .map(teamId => getTeamById(state, teamId))
          .filter(t => Boolean(t))) ||
      Immutable.List(),
    isEqual
  )

  const locations: LocationRecordMap = useSelector(
    (state: ReduxState) =>
      teams
        .map(({ location_id: locationId }) =>
          getLocationById(state, locationId)
        )
        .toSet(),
    isEqual
  )

  const labels: LabelRecordMap = useMemoSelector(state =>
    getLabelsByTeamId(state, teamId)
  )

  useEffect(() => {
    const team = teams.find(
      ({ location_id: teamLocationId }) => teamLocationId === selectedLocationId
    )
    // We should only update the team IF the location is changed as well.
    // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
    if (team && selectedTeamLocation?.id !== selectedLocationId) {
      setValue(
        'team',
        { value: team?.id, label: team?.name },
        { shouldDirty: true }
      )
      setTeamId(team?.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId])

  useEffect(() => {
    setTeamId(selectedTeamId)
  }, [selectedTeamId])

  const prevTeamId = usePrevious(teamId)
  useEffect(() => {
    if (prevTeamId) {
      setValue('label', defaultLabel, { shouldDirty: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId])

  const shiftStartDate = moment(shift.starts_at).format('YYYY-MM-DD')

  const isShiftLocked = Boolean(shift?.locked_at)

  const isUpdatingWeeklyschedule = useMemo(() => {
    const nextWeeklyschedule = filteredWeeklyschedules.find(
      weeklyschedule => weeklyschedule?.get('team_id') === teamId
    )
    return (
      shift &&
      nextWeeklyschedule &&
      nextWeeklyschedule?.id !== shift?.weeklyschedule_id
    )
  }, [filteredWeeklyschedules, teamId, shift])

  const breakArePaid = account.paid_breaks

  const haveMultipleUserWithError = userContracts.filter(
    option => option?.highlight
  ).length

  const displayFloatingBreaksWarning = useFloatingBreaksWarning({
    contractId: contract?.id,
    date: selectedDays[0]?.value,
    shiftTimeRange: watch('shift_time_range'),
    breaks: watch('breaks')
  })

  const displayEndContractOverrunWarning = useDisplayEndContractOverrunWarning({
    contractEnd: contract?.contract_end,
    date: selectedDays[0]?.value,
    shiftTimeRange: watch('shift_time_range')
  })

  // Used at multiple place
  const LabelFieldComponent = (
    <LabelSelectLegacy
      control={control}
      errors={errors}
      disabled={Boolean(shift.locked_at)}
      haveAccess={atLeast.director}
      labels={labels}
      selectedLocationId={selectedLocationId}
      setValue={setValue}
    />
  )

  /** Save action disabled handler, split for better visibility */
  const saveActionDisabled =
    formLoading || !selectedDays?.length || displayEndContractOverrunWarning

  let saveActionUnassignedDisabled = false
  let saveActionShiftDisabled = false
  if (
    userContracts?.length > 0 &&
    userContracts?.length === haveMultipleUserWithError
  ) {
    saveActionUnassignedDisabled = true
  }
  if (userContracts?.length === haveMultipleUserWithError) {
    saveActionShiftDisabled = true
  }

  return (
    <BaseForm
      methods={methods}
      onSubmit={onSubmit}
      className='flex grow flex-col'
    >
      <Modal.Content>
        {isShiftLocked && <LockedAlert />}
        {formError && <AlertErrorAdapter className='mb-5' error={formError} />}
        <UserContractFieldLegacy
          control={control}
          errors={errors}
          disabled={Boolean(shift?.locked_at) || isUpdatingWeeklyschedule}
          isUpdatingWeeklyschedule={isUpdatingWeeklyschedule}
          locations={locations}
          shiftDateISO={shiftStartDate}
          weeklyScheduleTeamId={weeklyScheduleTeamId}
          haveMultipleUserWithError={haveMultipleUserWithError}
          isCreation={isCreation}
          valueState={userContracts}
          setValue={setValue}
          selectedDays={selectedDays.map(day => day.value)}
        />
        {!isCreation && (
          <UpdateDayFieldLegacy
            weeklyschedule={weeklyschedule}
            shift={shift}
            control={control}
            errors={errors}
            setValue={setValue}
          />
        )}
        {isCreation && (
          <WeekdaysFields
            selectedDays={selectedDays}
            control={control}
            errors={errors}
            setValue={setValue}
            date={date}
          />
        )}
        <div className={styles.MainGroup}>
          {(!showingMore || isUpdatingContract) && LabelFieldComponent}
          <ShiftTimeRangeInputs
            control={control}
            errors={errors}
            disabled={isShiftLocked}
          />
          {displayEndContractOverrunWarning && (
            <Alert
              intent='semantic.red'
              switchAt='sm'
              title={t('component.endContractOverrunWarning.title')}
              className='mb-4'
            >
              {t('component.endContractOverrunWarning.warning')}
            </Alert>
          )}
          <AutomaticBreakRowsContainer
            isShiftLocked={isShiftLocked}
            isBreakPaid={breakArePaid}
            isManager={atLeast.manager}
            location={weeklyscheduleLocation}
            displayFloatingBreaksWarning={displayFloatingBreaksWarning}
            shiftId={shiftId}
          />
          <MealRowsContainerLegacy
            control={control}
            defaultSelectedOption={weeklyscheduleLocation?.default_meal_type}
            isShiftLocked={isShiftLocked}
          />
          <ShiftNote
            control={control}
            errors={errors}
            isShiftLocked={isShiftLocked}
          />
        </div>
        {weeklyScheduleIsPublished && (
          <NotifyUser
            control={control}
            errors={errors}
            isDisabled={Boolean(shift?.locked_at)}
          />
        )}
        {(locations.size > 1 || teams.size > 1) && !isCreation && (
          <ShowMore onClick={handleToggleLocation} showingMore={showingMore} />
        )}
        {showingMore && (
          <>
            <LocationAndTeamFieldsLegacy
              locations={locations}
              teams={teams}
              disabled={isShiftLocked || isUpdatingContract}
              selectedLocationId={selectedLocationId}
              control={control}
              errors={errors}
              setValue={setValue}
            />
            {isUpdatingContract && (
              <Alert intent='semantic.blue'>
                {t('shiftForm.trickModifyEmployee')}
              </Alert>
            )}
            {!isUpdatingContract && LabelFieldComponent}
          </>
        )}
      </Modal.Content>
      <Modal.Footer className='sticky bottom-0 bg-neutral-50 pt-6'>
        {!isShiftLocked && (
          <ShiftActions
            shiftId={shift?.id}
            formLoading={formLoading}
            onClickDelete={() =>
              onDeleteSubmit({ notify_user: watch('notify_user') })
            }
            createDisabled={
              saveActionDisabled ||
              saveActionShiftDisabled ||
              saveActionUnassignedDisabled
            }
            isCreation={isCreation}
          />
        )}
      </Modal.Footer>
    </BaseForm>
  )
}
